import { useAuth } from "@/contexts/AuthContext";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

/* Animated Meda eye (blue, light-theme variant of the login eye) */
function HeroEye() {
  return (
    <div className="relative mx-auto h-40 w-44 shrink-0 sm:h-52 sm:w-56 lg:h-56 lg:w-64" data-testid="meda-hero-eye">
      {/* soft glow */}
      <div className="meda-glow pointer-events-none absolute inset-0 m-auto h-3/4 w-3/4 rounded-full bg-blue-400/25 blur-2xl" />

      {/* orbital rings */}
      <div className="pointer-events-none absolute inset-0">
        <div className="meda-orbit absolute inset-0" style={{ animationDuration: "18s" }}>
          <div className="absolute inset-[6%] rounded-full border border-blue-400/40" style={{ transform: "scaleY(0.34) rotate(-18deg)" }} />
        </div>
        <div className="meda-orbit-rev absolute inset-0" style={{ animationDuration: "26s" }}>
          <div className="absolute inset-[2%] rounded-full border border-indigo-300/40" style={{ transform: "scaleY(0.28) rotate(14deg)" }} />
        </div>
      </div>

      {/* particles */}
      {[
        { t: "14%", l: "20%", d: "0s" },
        { t: "22%", l: "80%", d: "0.6s" },
        { t: "76%", l: "28%", d: "1.1s" },
        { t: "70%", l: "74%", d: "0.3s" },
        { t: "48%", l: "8%", d: "0.9s" },
      ].map((p, i) => (
        <span
          key={i}
          className="meda-twinkle absolute h-1 w-1 rounded-full bg-blue-500"
          style={{ top: p.t, left: p.l, animationDelay: p.d, boxShadow: "0 0 6px #2563EB" }}
        />
      ))}

      <svg viewBox="0 0 400 240" className="relative h-full w-full">
        <defs>
          <radialGradient id="heroIris" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#BFDBFE" />
            <stop offset="38%" stopColor="#3B82F6" />
            <stop offset="74%" stopColor="#1D4ED8" />
            <stop offset="100%" stopColor="#0B225E" />
          </radialGradient>
          <radialGradient id="heroIrisGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="heroLid" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="55%" stopColor="#1877F2" />
            <stop offset="100%" stopColor="#1E3A8A" />
          </linearGradient>
        </defs>

        <g fill="none" stroke="url(#heroLid)">
          <path d="M40,120 Q200,18 360,120 Q200,222 40,120 Z" strokeWidth="3.4" />
          <path d="M70,120 Q200,46 330,120 Q200,194 70,120 Z" strokeWidth="1.8" opacity="0.6" />
          <path d="M96,120 Q200,66 304,120 Q200,174 96,120 Z" strokeWidth="1.1" opacity="0.4" />
        </g>

        <g className="meda-pulse" style={{ transformOrigin: "200px 120px" }}>
          <circle cx="200" cy="120" r="78" fill="url(#heroIrisGlow)" />
          <circle cx="200" cy="120" r="54" fill="url(#heroIris)" />
          <circle cx="200" cy="120" r="58" fill="none" stroke="url(#heroLid)" strokeWidth="1.4" opacity="0.85" />
          <circle cx="200" cy="120" r="64" fill="none" stroke="url(#heroLid)" strokeWidth="0.8" opacity="0.5" />
          <circle cx="200" cy="120" r="24" fill="#08153A" />
          <circle cx="188" cy="106" r="6" fill="#fff" opacity="0.85" />
        </g>
      </svg>
    </div>
  );
}

/**
 * MedaHero — light "Facebook-style" greeting banner with the animated Meda eye.
 */
export default function MedaHero() {
  const { user } = useAuth();
  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div
      data-testid="meda-hero"
      className="relative mb-6 overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-[#EAF1FE] via-white to-[#EAF4FF] px-6 py-7 shadow-sm sm:px-9 sm:py-8"
    >
      <style>{`
        .meda-float { animation: meda-float 6s ease-in-out infinite; }
        .meda-glow { animation: meda-glow 4s ease-in-out infinite; }
        .meda-orbit { animation: meda-orbit linear infinite; }
        .meda-orbit-rev { animation: meda-orbit-rev linear infinite; }
        .meda-pulse { animation: meda-pulse 3.6s ease-in-out infinite; }
        .meda-twinkle { animation: meda-twinkle 2.6s ease-in-out infinite; }
      `}</style>

      {/* ambient glows */}
      <div className="pointer-events-none absolute -left-20 -top-10 h-56 w-56 rounded-full bg-blue-300/20 blur-[80px]" />
      <div className="pointer-events-none absolute right-16 top-0 h-64 w-64 rounded-full bg-indigo-300/20 blur-[90px]" />

      {/* "Meda" status chip */}
      <div className="absolute right-5 top-5 z-20 hidden items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-[#1877F2] sm:flex">
        <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
        Meda
      </div>

      <div className="relative z-10 flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-[#0F172A] sm:text-4xl">
            <span className="mr-1">👋</span> {greeting()},
            <br />
            <span className="text-[#1877F2]" data-testid="meda-hero-name">
              {firstName}
            </span>
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-700 sm:text-base">
            I&rsquo;m <span className="font-semibold text-[#0F172A]">Meda</span>,
            <br className="hidden sm:block" /> your AI Admission Director.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Here&rsquo;s your business overview for today.
          </p>
        </div>

        <div className="meda-float">
          <HeroEye />
        </div>
      </div>
    </div>
  );
}
